import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { MaskLines } from "@/components/site/Reveal";

const Shell = ({ icon, title, children, testid }) => (
  <main data-testid={testid} className="flex min-h-screen items-center bg-ink px-5 pt-20 text-sand sm:px-10">
    <div className="mx-auto w-full max-w-2xl py-20">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-ochre">{icon}</span>
      <MaskLines lines={[title]} className="mt-10 font-display text-5xl font-medium tracking-tight sm:text-7xl" />
      <div className="mt-6 text-sand/85 md:text-lg">{children}</div>
      <div className="mt-10 flex flex-wrap gap-4">
        <Link to="/" data-testid="payment-home-link" className="btn-ochre">Back to home</Link>
        <Link to="/donate" data-testid="payment-donate-link" className="btn-line border-sand/40 text-sand hover:bg-sand hover:text-ink">Donate page</Link>
      </div>
    </div>
  </main>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sid) { setState("error"); return undefined; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data?.payment_status === "paid") { setState("paid"); return; }
        if (data?.status === "expired") { setState("error"); return; }
      } catch { /* retry */ }
      tries += 1;
      if (tries >= 10) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid]);

  if (state === "paid") return <Shell testid="payment-success" icon={<Check className="h-7 w-7" />} title="Thank you.">Your donation was received. A receipt from Stripe is on its way to your inbox. Your support keeps our community strong.</Shell>;
  if (state === "checking") return <Shell testid="payment-checking" icon={<Loader2 className="h-7 w-7 animate-spin" />} title="Confirming…">Just a moment while we confirm your payment.</Shell>;
  if (state === "pending") return <Shell testid="payment-pending" icon={<Loader2 className="h-7 w-7" />} title="Almost there.">Your payment is still processing. You'll get an email receipt once it's confirmed.</Shell>;
  return <Shell testid="payment-error" icon={<X className="h-7 w-7" />} title="Something went wrong.">We couldn't confirm this payment. No money has been taken if checkout didn't finish — please try again.</Shell>;
}

export function PaymentCancel() {
  return <Shell testid="payment-cancel" icon={<X className="h-7 w-7" />} title="Donation cancelled.">No payment was made. You can try again any time.</Shell>;
}
